import React from 'react';
import { Card } from 'antd';
import * as Icons from '@ant-design/icons';

import { IPageData } from '../../interfaces/page';
import { usePageData } from '../../hooks/usePage';

type Theme = 'outlined' | 'filled' | 'twoTone';

const colors = ['#48bb78', '#336cfb', '#fc8b37', '#ed253d', '#ed64a6'];

const iconTypes = [
  'bell',
  'book',
  'bug',
  'bulb',
  'calculator',
  'build',
  'calendar',
  'camera',
  'car',
  'carry-out',
  'cloud',
  'code',
  'dislike',
  'environment',
  'experiment',
  'eye',
  'file',
  'filter',
  'fire',
  'flag',
  'folder',
  'folder-open'
];

const pageData: IPageData = {
  title: 'Icons options',
  fulFilled: true,
  breadcrumbs: [
    { title: 'Home', route: 'dashboard' },
    { title: 'UI Kit ', route: 'dashboard' },
    { title: 'Icons Options' }
  ]
};

// helpers
const toPascal = (s: string) =>
  s
    .split(/[-_ ]+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');

const getIconComponent = (slug: string, theme: Theme) => {
  const base = toPascal(slug); // e.g. 'carry-out' -> 'CarryOut'
  const suffix =
    theme === 'filled' ? 'Filled' : theme === 'twoTone' ? 'TwoTone' : 'Outlined';

  // try requested theme, then fallback to Outlined, then a generic question icon
  // @ts-ignore dynamic access to the icons bundle
  const Comp = (Icons as any)[`${base}${suffix}`]
    // @ts-ignore
    || (Icons as any)[`${base}Outlined`]
    // @ts-ignore
    || (Icons as any).QuestionCircleOutlined;

  // Whether our chosen component is truly a TwoTone one
  const isTwoTone = (Comp as any)?.displayName?.toLowerCase?.().includes('twotone');

  return { Comp, isTwoTone };
};

const IconsOptionsPage: React.FC = () => {
  usePageData(pageData);

  const renderIcon = (
    slug: string,
    props?: { style?: React.CSSProperties; theme?: Theme; twoToneColor?: string }
  ) => {
    const theme = props?.theme ?? 'outlined';
    const { Comp, isTwoTone } = getIconComponent(slug, theme);

    const { theme: _omitTheme, twoToneColor, ...rest } = props || {};

    // Only pass twoToneColor to actual TwoTone icons
    const finalProps =
      theme === 'twoTone' && isTwoTone
        ? { twoToneColor, ...rest }
        : { ...rest };

    return <Comp key={slug} {...finalProps} />;
  };

  const displayIcons = (
    icons: string[],
    props?: { style?: React.CSSProperties; theme?: Theme; twoToneColor?: string }
  ) => <div className='elem-list'>{icons.map((icon) => renderIcon(icon, props))}</div>;

  const coloredIcons = (
    icons: string[],
    props: { style?: React.CSSProperties } | undefined,
    palette: string[]
  ) => (
    <div className='elem-list'>
      {icons.map((icon, index) =>
        renderIcon(icon, {
          ...props,
          theme: 'twoTone',
          twoToneColor: palette[index % palette.length]
        })
      )}
    </div>
  );

  const smallFZ = { fontSize: 16 };
  const mediumFZ = { fontSize: 20 };
  const largeFZ = { fontSize: 24 };

  return (
    <>
      <Card title='Default icons'>{displayIcons(iconTypes, { style: null as any })}</Card>

      <Card title='Icon sizes'>
        <h6 className='section-title mt-0'>16px font size</h6>
        {displayIcons(iconTypes, { style: smallFZ })}

        <h6 className='section-title'>20px font size</h6>
        {displayIcons(iconTypes, { style: mediumFZ })}

        <h6 className='section-title'>24px font size</h6>
        {displayIcons(iconTypes, { style: largeFZ })}
      </Card>

      <Card title='Icon theme' className='mb-0'>
        <h6 className='section-title mt-0'>Outlined</h6>
        {displayIcons(iconTypes, { style: mediumFZ, theme: 'outlined' })}

        <h6 className='section-title'>Filled</h6>
        {displayIcons(iconTypes, { style: mediumFZ, theme: 'filled' })}

        <h6 className='section-title'>Two tones</h6>
        {displayIcons(iconTypes, { style: mediumFZ, theme: 'twoTone' })}

        <h6 className='section-title'>Two tones colored</h6>
        {coloredIcons(iconTypes, { style: mediumFZ }, colors)}
      </Card>
    </>
  );
};

export default IconsOptionsPage;
